import React from 'react';
import { IconButton, Stack, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useApi } from '../context/useApi';
import { useLocation, useNavigate } from 'react-router-dom';
import { SearchFnReturn, useBaseList } from '../hook/useBaseList';
import { ClusterFilters, ICluster } from '../models/interfaces';
import { convertStringsToSort, tranformSearchFiltersForBE } from '../utils/helpers';
import type { AxiosResponse } from 'axios';
import { Close, Done, Link, Warning } from '@mui/icons-material';
import { List } from '../shared/list';
import { format } from 'date-fns/format';
import ClustersFilters from '../components/ClusterFilters';
import { CustomTooltip } from '../shared/Tooltip/customTooltip';
import { useDialog } from '../context/useDialog';
import { useMessage } from '../context/useMessage';

const FILTER_STORE_KEY = 'cluster';

const Clusters = () => {
  const { state } = useLocation();
  const { defaultApi } = useApi();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { createSimpleConfirm } = useDialog();
  const { error, success } = useMessage();

  const searchFn = async (
    page: number,
    size: number,
    filters: ClusterFilters,
    sort?: string[],
  ): Promise<SearchFnReturn<ICluster>> => {
    filters = tranformSearchFiltersForBE(filters);
    let resp: AxiosResponse<any>;
    filters = { ...filters };
    if (sort && sort.length > 0) {
      resp = await defaultApi.clustersSearchPost({
        params: { page: page, size: size, sort },
        data: { ...filters },
      });
    } else {
      resp = await defaultApi.clustersSearchPost({
        params: { page: page, size: size },
        data: { ...filters },
      });
    }

    if (resp.status === 200) {
      return { count: resp.data?.data.totalElements ?? 0, list: resp.data.data?.content ?? [] };
    } else {
      throw Error('Errore');
    }
  };

  const {
    list,
    page,
    pageSize,
    count,
    isLoading,
    isPreviousData,
    handlePageChange,
    handlePageSizeChange,
    clearFilters,
    sort,
    onSortClick,
    filtersForm: formProps,
  } = useBaseList<ICluster, ClusterFilters>({
    searchFn: searchFn,
    overrideFilters: state?.stateFilters,
    defaultFilters: {},
    filterStoreKey: FILTER_STORE_KEY,
    apiOptions: { cacheTime: 20000 },
  });

  return (
    <>
      <Stack
        direction="row"
        ml={'20px'}
        alignItems={'center'}
        justifyContent="space-between"
        spacing={1}
      >
        <Stack direction={'row'} alignItems={'center'} spacing={4} height={'56px'}>
          <Typography variant="h4" color="inherit">
            {t('cluster.title')}
          </Typography>
        </Stack>
      </Stack>
      <ClustersFilters clearFilters={clearFilters} formProps={formProps} defaultFilters={{}} />
      <List
        maxheight="calc(100vh - 20px)"
        list={list}
        count={count}
        page={page}
        pageSize={pageSize}
        loading={isLoading}
        isPreviousData={isPreviousData}
        onPageChange={handlePageChange}
        onPageSizeChange={handlePageSizeChange}
        gridTemplateColumns={'3fr 6fr 1.5fr 1fr 1fr 1fr 0.5fr'}
        keyGetter={(e: any) => e.id as string}
        sortBy={convertStringsToSort(sort)}
        onSortClick={onSortClick}
        onItemClick={(e: ICluster) => {
          navigate('/manualMatch', { state: { stateFilters: { cluster_id: e.cluster_id } } });
        }}
        fields={[
          {
            title: t('cluster.id'),
            key: 'cluster_id',
            sortable: false,
          },
          {
            title: t('cluster.description'),
            key: 'descriptions',
            sortable: false,
            tooltip: true,
          },
          {
            title: t('cluster.lastUpdate'),
            valueGetter: (item: ICluster) => {
              return format(new Date(item.updated_at), 'dd/MM/yyyy hh:mm');
            },
            key: 'last_update',
            sortable: false,
          },
          {
            title: t('cluster.hasReview'),
            key: 'has_review',
            cellRenderer: (e: ICluster) => {
              return <IconButton color="primary">{e.has_review ? <Done /> : <Close />}</IconButton>;
            },
          },
          {
            title: t('cluster.managedByGksd'),
            key: 'managed_by_gksd',
            cellRenderer: (e: ICluster) => {
              return (
                <IconButton color="primary">{e.managed_by_gksd ? <Done /> : <Close />}</IconButton>
              );
            },
          },
          {
            title: t('cluster.fusion'),
            key: 'fusion_cluster',
            cellRenderer: (e: ICluster) => {
              return (
                <IconButton
                  color="primary"
                  onClick={(event) => {
                    event.preventDefault();
                    event.stopPropagation();
                    createSimpleConfirm({
                      title: t('info.warning'),
                      message: t('cluster.SureToFusion') + e.fusion_to_candidate,
                      onConfirm: () => {
                        defaultApi
                          .operationsFusePost({
                            data: { source: e.fusion_to_candidate, destination: e.cluster_id },
                          })
                          .then(() => {
                            success({ title: t('cluster.successMerge') });
                            setTimeout(() => {
                              formProps.submit();
                            }, 1000);
                          })
                          .catch(() => {
                            error({ title: t('error.error') });
                          });
                      },
                    });
                  }}
                >
                  {e.fusion_to_candidate ? (
                    <CustomTooltip offset={[0, -10]} title={t('cluster.makeFusion')}>
                      <Warning color="warning" />
                    </CustomTooltip>
                  ) : (
                    '-'
                  )}
                </IconButton>
              );
            },
          },
          {
            title: '',
            key: '',
            cellRenderer: (e: ICluster) => {
              return (
                <IconButton
                  color="primary"
                  onClick={(event) => {
                    event.preventDefault();
                    event.stopPropagation();
                    // let filters:ProductsFilters = {}
                    // filters.cluster_id =
                    navigate('/products', {
                      state: { stateFilters: { cluster_id: e.cluster_id } },
                    });
                  }}
                >
                  <Link />
                </IconButton>
              );
            },
          },
        ]}
        emptyListMessage={t('info.noResult')}
        keepRecentlyOpenedItem
        // lastOpenedItem={lastOpenedItem}
      />
    </>
  );
};

export default Clusters;
